import {batch, useDispatch, useSelector} from "react-redux";

import {getFavouriteObject} from "@pg-mono/favourites";
import {RequestState} from "@pg-mono/request-state";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../app/rp_reducer";
import {openModal} from "../../real_estate/state/subscribe_real_estate_modal_slice";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {addToFavourites, removeFromFavourites} from "../actions/manage_favourites";
import {addRealEstateToFavouritesSuccess, removeRealEstateFromFavouritesSuccess} from "../utils/alerts";
import {useFavoritesAuth} from "./use_favorites_auth";

export const useFavourites = () => {
    const dispatch = useDispatch();
    const favoritesAuth = useFavoritesAuth();
    const {isMobile} = useUserDevice();

    const favouriteOffers = useSelector((state: IRPStore) => state.favourite.favourites.offers);
    const favouriteProperties = useSelector((state: IRPStore) => state.favourite.favourites.properties);

    const favourites = useSelector((state: IRPStore) => state.favourite.favourites);
    const fetchRequestState = useSelector((state: IRPStore) => state.favourite.fetchRequest);
    const isAuthenticated = useIsAuthenticated();

    //  In case user isn't authenticated, favourites are taken from localStorage
    const isLoaded = isAuthenticated ? fetchRequestState === RequestState.Success || fetchRequestState === RequestState.Error : favourites.initialized;

    return {
        favouritePropertiesIds: favouriteProperties,
        favouriteOfferIds: favouriteOffers,
        loaded: isLoaded,
        fetchRequest: fetchRequestState,
        isOfferInFavourites: (offerId: number) => {
            if (fetchRequestState === RequestState.Waiting || fetchRequestState === RequestState.Error) {
                return false;
            }

            return favouriteOffers.includes(offerId);
        },
        isPropertyInFavourites: (propertyId: number) => {
            if (fetchRequestState === RequestState.Waiting || fetchRequestState === RequestState.Error) {
                return false;
            }

            return favouriteProperties.includes(propertyId);
        },
        addPropertyToFavourites: async (propertyId: number, _offerId: number, priceAlert?: boolean) => {
            const favouriteObject = getFavouriteObject({propertyId});
            const favourite = {...favouriteObject, price_alerts: !!priceAlert};

            // Always add to favorites first (for unauthenticated users, this adds to localStorage)
            batch(() => {
                dispatch(addToFavourites([favourite]));
                if (isAuthenticated) {
                    dispatch(openModal());
                }
            });

            if (!isAuthenticated) {
                favoritesAuth({
                    type: "property"
                });
            }

            // Displaying the toast on the modal looks weird, so we skip it for unauthenticated users on mobile.
            if (isAuthenticated || !isMobile) {
                dispatch(addRealEstateToFavouritesSuccess);
            }
        },
        addPropertyToFavouritesWithoutAuthCheck: async (propertyId: number, _offerId: number, priceAlert?: boolean, hideNotification: boolean = false) => {
            const favouriteObject = getFavouriteObject({propertyId});
            const favourite = {...favouriteObject, price_alerts: !!priceAlert};

            batch(() => {
                dispatch(addToFavourites([favourite]));
                !hideNotification && dispatch(addRealEstateToFavouritesSuccess);
            });
        },
        removePropertyFromFavourites: async (propertyId: number) => {
            const favourite = getFavouriteObject({propertyId});

            batch(() => {
                dispatch(removeFromFavourites([favourite]));
                dispatch(removeRealEstateFromFavouritesSuccess);
            });
        },
        addOfferToFavourites: async (offerId: number) => {
            const favourite = getFavouriteObject({offerId});

            // Always add to favorites first (for unauthenticated users, this adds to localStorage)
            batch(() => {
                dispatch(addToFavourites([favourite]));
                dispatch(addRealEstateToFavouritesSuccess);
                if (isAuthenticated) {
                    dispatch(openModal());
                }
            });

            if (!isAuthenticated) {
                favoritesAuth({
                    type: "offer"
                });
            }
        },
        addOfferToFavouritesWithoutAuthCheck: async (offerId: number, hideNotification: boolean = false) => {
            const favourite = getFavouriteObject({offerId});

            // Always add to favorites first (for unauthenticated users, this adds to localStorage)
            batch(() => {
                dispatch(addToFavourites([favourite]));
                !hideNotification && dispatch(addRealEstateToFavouritesSuccess);
            });
        },
        removeOfferFromFavourites: async (offerId: number) => {
            const favourite = getFavouriteObject({offerId});

            batch(() => {
                dispatch(removeFromFavourites([favourite]));
                dispatch(removeRealEstateFromFavouritesSuccess);
            });
        }
    };
};
